import Box from '@mui/material/Box';
import agent from '@root/agent';
import { useCloudProvider } from '@root/components/v2/content/CloudProviderBar/CloudProviderContext';
import PageContainer from '@root/components/v2/layout/PageContainer';
import PageContent from '@root/components/v2/layout/PageContent';
import { useSelectedEnvironments } from '@root/hooks/environments';
import useTimeFilter from '@root/hooks/SelectTimeFilter';
import HeaderBarNew from '@root/components/HeaderBarNew';
import useEnhancedQuery from '@root/query/useEnhancedQuery';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useDebouncedValue from '@root/hooks/useDebouncedValue';
import useSortData from '@root/hooks/useSortData';
import MuiTable, { DEFAULT_PAGE_SIZE, DEFAULT_ROWS_PER_PAGE_OPTIONS } from '@root/components/table/table';
import { findingsColumns } from './columns';
import FindingDetailsOverlay from './FindingDetails';
import ActionModal from '../../risk-acceptance/ActionModal';
import { formatTimeFilter } from '@root/hooks/features';
import PaginatedFilter from '@root/components/FilterNew/PaginatedFilter';
import { toast } from 'sonner';
import { useQuery } from '@root/hooks/useQuery';
import { updateNotificationBanner } from '@root/components/notification/actions';
import { useDispatch } from 'react-redux';
import { CustomCursorFooter } from '@root/components/table/CustomCursorFooter';
import isEmpty from 'lodash/isEmpty';
import UnscannedImagesTable from '../UnscannedImages';
import { Popover, PopoverContent, PopoverTrigger, PopoverClose } from '@root/components/v3/popover';
import { Download, FileDown, CalendarClock } from 'lucide-react';
import CveFilterCard from '../CveFilterCard';
import ScheduleReportDialog from '../CVEs/ScheduleReportDialog';
import { funnelStageFilters } from '../funnelStageFilters';

const workloadGenerator = v => {
  const workloads = v.runtime_instances?.map(item => `${v.namespace}/${item.pod_name}/${item.container_name}`);
  return workloads;
};

const Findings = ({ isEnabled, riskSpotlight }) => {
  const { cloudProvider } = useCloudProvider();
  const [searchString, setSearchString] = useState('');
  const debouncedSearchString = useDebouncedValue(searchString, 750);

  // Cursor Pagination State
  const [currentPage, setCurrentPage] = useState(0); // Start from 0 for cursor pagination
  const [cursors, setCursors] = useState([null]); // Stack of cursors, null for first page
  const [hasNextPage, setHasNextPage] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { sortData, handleSortModelChange } = useSortData();
  const { selectedEnvironments } = useSelectedEnvironments();
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const query = useQuery();
  const unscannedImages = query.get('unscannedImages');
  const [openFilter, setOpenFilter] = useState(false);
  const dispatch = useDispatch();

  const [openScheduleDialog, setOpenScheduleDialog] = useState(false);
  const { timeFilter } = useTimeFilter();
  const [selectedFilters, setSelectedFilters] = useState(() => {
    const funnelFilters = funnelStageFilters(query);
    const severityParam = query.get('severity');
    return {
      ...funnelFilters,
      // The funnel's Critical stage sets the severity; otherwise it comes from the dashboard's severity legend.
      severity: funnelFilters.severity.length || !severityParam ? funnelFilters.severity : [severityParam],
      artifact_type: [],
      cve_type: [],
      status: ['active'],
    };
  });
  const [openModal, setOpenModal] = useState(false);
  const [selectedActionData, setSelectedActionData] = useState(null);

  // Cluster list for Schedule Report dialog
  const { data: clusterListData, isFetching: isFetchingClusterList } = useEnhancedQuery(
    ['get_cluster_list_cves', selectedEnvironments.map(e => e.id), cloudProvider],
    () =>
      agent.Kubernetes.get_cluster_list({
        environments: selectedEnvironments.map(e => e.id),
        clusters: [],
        date_filter: formatTimeFilter(timeFilter),
        cloud_provider: cloudProvider,
      }),
  );

  const clusterList = useMemo(() => {
    if (isFetchingClusterList) return [];
    return clusterListData ?? [];
  }, [clusterListData, isFetchingClusterList]);

  // Updated query with cursor
  const { data: allDataK8, isFetching: isFetchingAllDataK8 } = useEnhancedQuery(
    [
      'get_vulnerability_datak8',
      debouncedSearchString,
      selectedEnvironments.map(item => item.id),
      currentPage, // Added to trigger refetch when page changes
      cursors[currentPage], // Cursor for this page
      pageSize,
      cloudProvider,
      sortData,
      isEnabled,
      selectedFilters,
    ],
    () =>
      agent.Vulnerabilites.get_vulnerability_datak8({
        environments: selectedEnvironments.map(item => item.id),
        sort: isEmpty(sortData)
          ? [
              {
                field: 'severity',
                order: 'asc',
              },
            ]
          : sortData?.map(item => ({
              field: item.key,
              order: item.value,
            })),
        clusters: selectedFilters?.clusters || [],
        severity: selectedFilters?.severity || [],
        fixable: selectedFilters?.fixable || [],
        cvss_attack_vector: selectedFilters.cvss_attack_vector || [],
        epss_score: selectedFilters.epss_score || [],
        exploitable: selectedFilters?.exploitable || [],
        in_use: selectedFilters?.in_use || [],
        is_public_exposed: selectedFilters?.is_public_exposed || [],
        epss_severe: selectedFilters?.epss_severe || [],
        status: selectedFilters?.status || [],
        workload: selectedFilters?.workload || [],
        component: selectedFilters?.component || [],
        artifact_type: selectedFilters?.artifact_type || [],
        cve_type: selectedFilters?.cve_type || [],
        image: selectedFilters?.image || [],
        search_query: debouncedSearchString ? debouncedSearchString : '',
        cursor: currentPage === 0 ? null : cursors[currentPage], // First page has no cursor
        rows_per_page: pageSize,
        risk_spotlight: isEnabled,
        cve_id: selectedFilters?.cve_id || [],
        export: false,
      }),
  );

  useEffect(() => {
    if (isFetchingAllDataK8) return;
    if (allDataK8?.pagination) {
      const { has_next, next_cursor } = allDataK8.pagination;
      setHasNextPage(has_next);

      // Store next cursor if we don't have it yet
      if (has_next && next_cursor && cursors.length === currentPage + 1) {
        setCursors(prev => [...prev, next_cursor]);
      }
    }
  }, [allDataK8, currentPage, cursors.length, isFetchingAllDataK8]);

  // Reset pagination when filters/sort/search changes
  useEffect(() => {
    setCurrentPage(0);
    setCursors([null]); // First page has no cursor
  }, [debouncedSearchString, selectedFilters, sortData, selectedEnvironments, cloudProvider, isEnabled]);

  const fetchKubernetesVulCVEFilterPropertyValues = useCallback(
    async paginatedFilter => {
      const response = await agent.Kubernetes.get_cve_filter_data({
        environments: selectedEnvironments.map(item => item.id),
        clusters: [],
        search_key: paginatedFilter ? paginatedFilter?.search_key : '',
        filter_particular_key: paginatedFilter
          ? paginatedFilter?.filter_particular_key?.toLowerCase().replace(/\s+/g, '_')
          : '',
        previous_filter: [],
        page_no: paginatedFilter ? paginatedFilter?.page_no : 1,
        row_per_page: paginatedFilter ? paginatedFilter?.row_per_page : 20,
      });
      return response;
    },
    [selectedEnvironments],
  );

  const propertyValues = useMemo(() => {
    return {
      Clusters: { data: [], num_records: 0, has_more: false },
      Severity: { data: [], num_records: 0, has_more: false },
      'CVE ID': { data: [], num_records: 0, has_more: false },
      Workload: { data: [], num_records: 0, has_more: false },
      Component: { data: [], num_records: 0, has_more: false },
      Image: { data: [], num_records: 0, has_more: false },
      Fixable: { data: [], num_records: 0, has_more: false },
      'In Use': { data: [], num_records: 0, has_more: false },
      'Is Public Exposed': { data: [], num_records: 0, has_more: false },
      Exploitable: { data: [], num_records: 0, has_more: false },
      'CVSS Attack Vector': { data: [], num_records: 0, has_more: false },
      'EPSS Score': { data: [], num_records: 0, has_more: false },
      'EPSS Severe': { data: [], num_records: 0, has_more: false },
      'Artifact Type': { data: [], num_records: 0, has_more: false },
      'CVE Type': { data: [], num_records: 0, has_more: false },
      Status: { data: [], num_records: 0, has_more: false },
    };
  }, []);

  // Cursor pagination handlers
  const handleNextPage = useCallback(() => {
    if (hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  }, [hasNextPage]);

  const handlePrevPage = useCallback(() => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  }, [currentPage]);

  const handlePageSizeChange = useCallback(_pageSize => {
    setPageSize(_pageSize);
    setCurrentPage(0);
    setCursors([null]); // Reset cursors
    setHasNextPage(false);
  }, []);

  const handleRowClick = useCallback(r => {
    // if (isEmpty(r.row?.workload_id) && isEmpty(r.row?.image_id) && isEmpty(r.row?.risk_spotlight)) {
    //   setShowDetailsPanel(false);
    //   setSelectedRow(null);
    //   toast.error(`No Workload/Image details available for this ${r.row?.cve_id}`);
    //   return;
    // }
    setSelectedRow(r.row);
    setShowDetailsPanel(true);
  }, []);

  const handleCloseDetailsPanel = useCallback(() => {
    setShowDetailsPanel(false);
    setSelectedRow(null);
  }, []);

  const handleOpenModal = useCallback(() => {
    setOpenModal(true);
  }, []);

  const handleCLoseModal = useCallback(() => {
    setOpenModal(false);
  }, []);

  const handleExportData = useCallback(async () => {
    updateNotificationBanner(dispatch, {
      status: 'inProgress',
      msg: 'Please wait while we are preparing your download',
      id: 'kubernetes_vulnerability_reports_download',
    });
    try {
      const response = await agent.Vulnerabilites.get_vulnerability_datak8_export({
        environments: selectedEnvironments.map(item => item.id),
        sort: sortData?.map(item => ({
          field: item.key,
          order: item.value,
        })),
        clusters: selectedFilters?.clusters || [],
        severity: selectedFilters?.severity || [],
        fixable: selectedFilters?.fixable || [],
        cvss_attack_vector: selectedFilters.cvss_attack_vector || [],
        epss_score: selectedFilters.epss_score || [],
        status: selectedFilters.status || [],
        exploitable: selectedFilters?.exploitable || [],
        search_query: debouncedSearchString ? debouncedSearchString : '',
        in_use: selectedFilters?.in_use || [],
        is_public_exposed: selectedFilters?.is_public_exposed || [],
        epss_severe: selectedFilters?.epss_severe || [],
        workload: selectedFilters?.workload || [],
        component: selectedFilters?.component || [],
        image: selectedFilters?.image || [],
        rows_per_page: pageSize,
        artifact_type: selectedFilters?.artifact_type || [],
        cve_type: selectedFilters?.cve_type || [],
        risk_spotlight: isEnabled,
        cve_id: selectedFilters?.cve_id || [],
        export: true,
        unique_id: localStorage.getItem('uniqueUserId'),
      });
      if (response?.data) {
        toast.success(response?.data);
      }
    } catch (err) {
      toast.error(`Failed to export data. ${err.message}`);
    }
  }, [dispatch, selectedEnvironments, sortData, selectedFilters, debouncedSearchString, pageSize, isEnabled]);

  const _tableData = useMemo(() => {
    if (isFetchingAllDataK8 || !allDataK8?.data) return [];
    return allDataK8?.data?.map((v, i) => ({ ...v, id: i, affected_workloads: workloadGenerator(v) })) ?? [];
  }, [allDataK8?.data, isFetchingAllDataK8]);

  return (
    <PageContainer>
      {/* Outside PageContent, so the spotlight keeps the inset it has on the other tabs. */}
      {!unscannedImages && (
        <>
          <Box px="15px" pt="15px">
            <CveFilterCard selectedFilters={selectedFilters} setSelectedFilters={setSelectedFilters} />
          </Box>
          {riskSpotlight}
        </>
      )}
      <PageContent>
        {!unscannedImages ? (
          <>
            <HeaderBarNew
              marginTop={'0px'}
              marginBottom={'13px'}
              title={''}
              searchBar
              setOpenFilter={setOpenFilter}
              openFilter={openFilter}
              searchBarOnChange={e => setSearchString(e.target.value)}
              searchBarValue={searchString}
              setSearchTerm={setSearchString}
              buttonGroup={
                <Popover>
                  <PopoverTrigger asChild>
                    <div
                      style={{
                        fill: 'var(--headerbar-text-color)',
                        background: 'var(--headerbar-icon-container-bg)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        height: '33px',
                        width: '35px',
                        marginLeft: '10px',
                        flexShrink: 0,
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--headerbar-icon-container-hover)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'var(--headerbar-icon-container-bg)')}>
                      <Download style={{ height: '20px' }} />
                    </div>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    className="w-[200px] p-1"
                    style={{
                      border: '1px solid var(--headerbar-border-color)',
                      borderRadius: '8px',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                      animationDuration: '0ms',
                      animationDelay: '0ms',
                    }}>
                    <PopoverClose asChild>
                      <button
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                        style={{
                          color: 'var(--headerbar-text-color)',
                          fontFamily: 'Manrope',
                          border: 'none',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                        onClick={handleExportData}>
                        <FileDown size={15} style={{ flexShrink: 0 }} />
                        Export CVE
                      </button>
                    </PopoverClose>
                    <PopoverClose asChild>
                      <button
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                        style={{
                          color: 'var(--headerbar-text-color)',
                          fontFamily: 'Manrope',
                          border: 'none',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                        onClick={() => setOpenScheduleDialog(true)}>
                        <CalendarClock size={15} style={{ flexShrink: 0 }} />
                        Schedule the Report
                      </button>
                    </PopoverClose>
                  </PopoverContent>
                </Popover>
              }
            />

            <PaginatedFilter
              openFilter={openFilter}
              option={propertyValues}
              setSelectedFilters={setSelectedFilters}
              selectedFilters={selectedFilters}
              fetchFilterPropertyValues={fetchKubernetesVulCVEFilterPropertyValues}
              defaultValue={{}}
            />

            <div style={{ position: 'relative', display: 'flex', flexDirection: 'row' }}>
              <MuiTable
                columns={findingsColumns(handleOpenModal, setSelectedActionData, true)}
                rows={_tableData}
                LoaderState={isFetchingAllDataK8}
                onRowClick={handleRowClick}
                paginationMode="server"
                sortingMode="server"
                total_size={-1} // Not used with cursor pagination
                handleSortModelChange={handleSortModelChange}
                pageSize={pageSize}
                noDataMessage={'No CVE present!'}
                onPageSizeChange={handlePageSizeChange}
                rowHeight={49.9}
                tableHeight={'637px'}
                cursorPagination={true}
                // Cursor pagination specific props
                currentPage={currentPage}
                hasNextPage={hasNextPage}
                onNextPage={handleNextPage}
                onPrevPage={handlePrevPage}
                components={{
                  Footer: () => (
                    <CustomCursorFooter
                      currentPage={currentPage}
                      hasNextPage={hasNextPage}
                      onNextPage={handleNextPage}
                      onPrevPage={handlePrevPage}
                      loading={isFetchingAllDataK8}
                      pageSize={pageSize}
                      onPageSizeChange={handlePageSizeChange}
                      rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                    />
                  ),
                }}
              />
            </div>

            {/* Findings has its own overlay (attack path, risk analysis); CVEs keeps the shared one. Keyed by
                row, so every finding opens on its Overview tab. */}
            {showDetailsPanel && (
              <FindingDetailsOverlay
                key={selectedRow?.id}
                open={showDetailsPanel}
                onClose={handleCloseDetailsPanel}
                selectedRow={selectedRow}
              />
            )}

            {openModal && <ActionModal open={openModal} onClose={handleCLoseModal} selectedRow={selectedActionData} />}

            {openScheduleDialog && (
              <ScheduleReportDialog
                open={openScheduleDialog}
                onClose={() => setOpenScheduleDialog(false)}
                clusterListData={clusterList?.data || []}
              />
            )}
          </>
        ) : (
          <UnscannedImagesTable isEnabled={isEnabled} />
        )}
      </PageContent>
    </PageContainer>
  );
};

export default Findings;
